/**
 * Single source of truth for the facts search engines and LLM answer engines will quote.
 *
 * Per-route <head> tags (Seo.jsx), the JSON-LD graph (structuredData.js) and the static
 * public/llms.txt all describe the same product, so they are all derived from here. If a
 * price or a claim changes, it changes once.
 *
 * NOTE: public/llms.txt and public/sitemap.xml are static files and cannot import this
 * module. `npm run seo:check` verifies they still agree with these values.
 */

export const SITE_URL = "https://badgereg.com";
export const DEMO_FALLBACK_URL = "https://demo.badgereg.com";

/** Facts repeated verbatim across meta descriptions, JSON-LD and llms.txt. */
export const PRODUCT = {
  name: "BadgeReg",
  legalName: "BadgeReg",
  // Deliberately leads with the words a prospect or an LLM would actually match on:
  // Scouting, merit badge college, troop/crew. The previous copy said none of them.
  tagline:
    "Merit badge college registration and fundraising software for Scout troops",
  shortDescription:
    "BadgeReg is registration and fundraising software for Scouting merit badge colleges. Troops and crews use it to publish class schedules, let parents register their Scouts online, collect payments through PayPal, and track income and attendance.",
  audience:
    "Scouts BSA troops, crews, and other Scouting units running merit badge colleges",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  currency: "USD",
  setupFee: 250,
  perScoutFee: 2.5,
  paymentProcessor: "PayPal",
  contactEmail: "receipts@badgereg.com",
  supportEmail: "support@badgereg.com",
  tenantDomainPattern: "your-event.badgereg.com",
  author: {
    name: "Jacob Byerline",
    url: "https://byerline.me",
  },
};

/**
 * Prose pricing sentence. Answer engines quote sentences, not pricing-card layout, so this
 * exact string is rendered on /pricing and repeated in llms.txt.
 */
export const PRICING_SENTENCE =
  "BadgeReg costs a one-time $250 setup fee per event plus $2.50 per registered Scout. " +
  "There is no monthly subscription and no per-unit licence. Payments are processed through " +
  "PayPal and PayPal's processing fee is included in the amount collected at checkout.";

const ogImage = `${SITE_URL}/og-image.png`;

/**
 * Per-route <head> content. `path` doubles as the sitemap entry list.
 * Titles stay under ~60 characters and descriptions under ~155 so neither is truncated.
 */
export const ROUTE_META = {
  "/": {
    path: "/",
    title: "BadgeReg | Merit Badge College Registration for Scout Troops",
    description:
      "Registration and fundraising software for Scouting merit badge colleges. Publish class schedules, register Scouts online, collect PayPal payments, and track income.",
    priority: "1.0",
    changefreq: "monthly",
  },
  "/pricing": {
    path: "/pricing",
    title: "Pricing | BadgeReg Merit Badge College Software",
    description:
      "BadgeReg costs $250 per event plus $2.50 per registered Scout. No subscription, no per-unit licence. Most merit badge college fundraisers raise upwards of $10,000.",
    priority: "0.9",
    changefreq: "monthly",
  },
  "/demo": {
    path: "/demo",
    title: "Live Demo | Try BadgeReg Merit Badge Registration",
    description:
      "Explore a working BadgeReg merit badge college without signing up. Browse classes, register a Scout, and see the organizer dashboard, reports, and payment ledger.",
    priority: "0.9",
    changefreq: "weekly",
  },
  "/faqs": {
    path: "/faqs",
    title: "FAQs | BadgeReg Merit Badge College Software",
    description:
      "How much BadgeReg costs, how Scouts pay, whether you need your own PayPal account, how registrations are exported, and how to get your troop started.",
    priority: "0.8",
    changefreq: "monthly",
  },
  "/contact": {
    path: "/contact",
    title: "Contact Us | Get Started with BadgeReg",
    description:
      "Get in touch to set up a merit badge college for your troop or crew, ask a question about pricing, request a feature, or sponsor development.",
    priority: "0.7",
    changefreq: "yearly",
  },
  "/about": {
    path: "/about",
    title: "About BadgeReg | Built by Scouting Volunteers",
    description:
      "BadgeReg is built by Scouting volunteers who ran merit badge colleges on spreadsheets and paper forms. Who we are, why the software exists, and how it is funded.",
    priority: "0.6",
    changefreq: "yearly",
  },
  "/security": {
    path: "/security",
    title: "Security | How BadgeReg Protects Your Data",
    description:
      "How BadgeReg protects Scout and parent data: encryption in transit, role-based access, PayPal-handled card details, isolated event data, backups, and breach response.",
    priority: "0.6",
    changefreq: "yearly",
  },
  "/privacy": {
    path: "/privacy",
    title: "Privacy Policy | BadgeReg",
    description:
      "What personal data BadgeReg collects from Scouts, parents and organizers, why it is collected, who it is shared with, how long it is kept, and how to have it deleted.",
    priority: "0.4",
    changefreq: "yearly",
  },
  "/terms": {
    path: "/terms",
    title: "Terms of Service | BadgeReg",
    description:
      "The terms covering use of BadgeReg: fees, payment processing through PayPal, event cancellation and refunds, acceptable use, data ownership, and limits of liability.",
    priority: "0.4",
    changefreq: "yearly",
  },
  "/accessibility": {
    path: "/accessibility",
    title: "Accessibility | BadgeReg",
    description:
      "BadgeReg's site targets WCAG 2.2 Level AA, checked on every build. What is covered, the limitations we know about, and how to tell us when something blocks you.",
    priority: "0.4",
    changefreq: "yearly",
  },
};

/**
 * Effective date shown on the legal pages.
 *
 * One constant rather than a date typed into each page, because a policy whose stated date disagrees
 * between two pages invites exactly the question you least want asked about a policy.
 */
export const POLICY_LAST_UPDATED = "July 27, 2026";

export const DEFAULT_META = {
  title: `${PRODUCT.name} | ${PRODUCT.tagline}`,
  description: PRODUCT.shortDescription,
  ogImage,
};

export const OG_IMAGE = ogImage;

/** Absolute URL for a route path, used for canonical and og:url. */
export function absoluteUrl(path = "/") {
  if (path === "/") {
    return `${SITE_URL}/`;
  }
  return `${SITE_URL}${path}`;
}
